/**
 * HB-5 — the block-update override in the CREATE WIZARD's vocabulary.
 *
 * The override accordion stopped being a data-source tree (audiences → actors →
 * tasks → reviews) and became the wizard's two selection tables: insight theses
 * and social proof. This module holds the pure rules those tables need, so the
 * dialog stays layout and the rules stay testable:
 *
 *  - D-L2-11 — the UNTOUCHED dialog preselects exactly what the server would
 *    send with no override: the head's FROZEN `inputManifest.insightSelection`
 *    for theses (the full projection when the head narrowed nothing) and the
 *    strategy's own social-proof rows. The block's `dataUsage` citations only
 *    ADD the provenance chip and the sort — they never narrow the tick (C7-1:
 *    preselecting the 5 cited rows while the server uses 11 would turn the first
 *    tick into a silent truncation).
 *  - invariant 4 — a cited row sorts first and carries «from AI Data Usage».
 */
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import {
  GLOBAL_INSIGHT_SCOPE,
  scopeWithoutThesesMessage,
  type FeasibilityInsightScope,
} from '@modules/shared-types';
import { parseInsightCitationPath } from '../utils/insightCitationPath';
import { extractSourceDataPaths } from './blockSourceDataPaths';

/** The head's frozen narrowing, as `inputManifest.insightSelection` carries it. */
export interface FrozenInsightSelection {
  insightIds?: string[];
  categories?: string[];
}

/** What this block actually cited on its last generation. */
export interface BlockCitedIds {
  /** Thesis ids of `@insight[a].<category>[id]` citations. */
  thesisIds: string[];
  /** Entity ids of `@review[…]` / `@rating[…]` / `@studyCase[…]` / `@bigNumber[…]`. */
  socialProofIds: string[];
}

const SOCIAL_PROOF_PREFIXES = ['@review[', '@rating[', '@studyCase[', '@bigNumber['] as const;

/**
 * Name-keyed citations (the generator writes `@rating[AI Calorie Tracker … App
 * Store]`) simply fail to match a row id — they lose their chip, never a tick.
 */
export function blockCitedIds(block: StrategyBlock | null): BlockCitedIds {
  const thesisIds = new Set<string>();
  const socialProofIds = new Set<string>();
  for (const path of extractSourceDataPaths(block)) {
    const citation = parseInsightCitationPath(path);
    if (citation) {
      thesisIds.add(citation.insightId);
      continue;
    }
    const prefix = SOCIAL_PROOF_PREFIXES.find((candidate) => path.startsWith(candidate));
    if (!prefix) continue;
    const end = path.indexOf(']', prefix.length);
    if (end > prefix.length) socialProofIds.add(path.slice(prefix.length, end));
  }
  return { thesisIds: [...thesisIds], socialProofIds: [...socialProofIds] };
}

export interface ThesisPreload {
  /** The ticked ids, in row order. */
  ids: string[];
  /** True when the head froze a narrowing; false = the analysis' own projection. */
  narrowed: boolean;
}

/**
 * The tick the dialog opens with. Mirrors `StrategyFormDialog`'s edit-mode
 * preload of the same manifest (categories expand to their theses), so the two
 * surfaces read one strategy the same way.
 */
export function preloadedThesisIds(
  rows: Array<{ id: string; category: string }>,
  frozen: FrozenInsightSelection | undefined,
): ThesisPreload {
  const hasIds = Array.isArray(frozen?.insightIds);
  const hasCategories = Array.isArray(frozen?.categories);
  if (!frozen || (!hasIds && !hasCategories)) {
    return { ids: rows.map((row) => row.id), narrowed: false };
  }
  const ticked = new Set(frozen.insightIds ?? []);
  const categories = frozen.categories ?? [];
  for (const row of rows) {
    if (categories.includes(row.category)) ticked.add(row.id);
  }
  return { ids: rows.filter((row) => ticked.has(row.id)).map((row) => row.id), narrowed: true };
}

/** Ids the strategy's own source data carries — the social-proof preload. */
export function strategySocialProofIds(sourceData: unknown): string[] {
  const record = (sourceData ?? {}) as Record<string, unknown>;
  const ids: string[] = [];
  for (const key of ['reviews', 'ratings', 'studyCases', 'bigNumbers']) {
    const collection = record[key];
    if (!Array.isArray(collection)) continue;
    for (const entry of collection) {
      const id = (entry as { id?: unknown } | null)?.id;
      if (typeof id === 'string' && id) ids.push(id);
    }
  }
  return [...new Set(ids)];
}

/**
 * Preload ∩ rows, in ROW order — an id whose entity has been deleted is not
 * offered and must not read as dirty, and the order has to match what
 * `preloadedThesisIds` produces or the comparison below would depend on which
 * list a row came from.
 */
export function intersectWithRows(ids: string[], rows: Array<{ id: string }>): string[] {
  const wanted = new Set(ids);
  return rows.filter((row) => wanted.has(row.id)).map((row) => row.id);
}

/** Invariant 4: the rows this block cited come first, everything else keeps its order. */
export function sortCitedFirst<R extends { id: string }>(rows: R[], cited: Set<string>): R[] {
  if (cited.size === 0) return rows;
  const first: R[] = [];
  const rest: R[] = [];
  for (const row of rows) (cited.has(row.id) ? first : rest).push(row);
  return [...first, ...rest];
}

/** Order-insensitive comparison — the override is derived, never latched. */
export function sameIdSet(a: string[], b: string[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every((id) => setB.has(id));
}

/** One thesis row, as far as the scope rules are concerned. */
export interface ScopedThesisRow {
  id: string;
  scope: 'global' | 'task';
  taskIds: string[];
  /** «Global», or the task names of `taskIds` joined by ` · ` (the wizard's shape). */
  scopeLabel: string;
}

/**
 * Task id → task name, zipped out of the rows. The block dialog has no task
 * list of its own; `scopeLabel` is built by `useWizardSourceRows` as
 * `taskIds.map(name).join(' · ')`, so position i of the split IS `taskIds[i]`.
 */
function taskNamesOf(rows: ScopedThesisRow[]): Record<string, string> {
  const names: Record<string, string> = {};
  for (const row of rows) {
    if (row.scope === 'global') continue;
    const labels = row.scopeLabel.split(' · ');
    row.taskIds.forEach((taskId, index) => {
      if (!names[taskId] && labels[index]) names[taskId] = labels[index];
    });
  }
  return names;
}

/**
 * Ruling HB5-R2 — the client guard mirrors the SERVER's resolution rule, not the
 * wizard's create-time rule.
 *
 * `strategyInputManifest.ts` → `scopeIsNarrowed` (≈659): the global and per-task
 * id namespaces are disjoint (W13-R22), so with no `scopes` key — which is the
 * only shape this dialog sends — a scope is ADDRESSED by a selection when at
 * least one selected thesis actually lives in it. A scope the selection does not
 * address keeps its own projection (or, per FR-3, the head's frozen narrowing);
 * it is a legal, fully resolvable state, never a refusal.
 *
 * A scope is therefore starved only when the selection the operator STARTED from
 * addressed it and their edit left it with nothing: that state cannot be
 * expressed on the wire at all (dropping its last id silently turns «narrow this
 * scope to nothing» into «say nothing about this scope», which restores exactly
 * the grounding the operator was trying to remove), so the dialog refuses it
 * instead of sending a run that contradicts what it shows.
 */
function scopeOf(row: ScopedThesisRow): string[] {
  return row.scope === 'global' ? [GLOBAL_INSIGHT_SCOPE] : row.taskIds;
}

/** The server's `scopeIsNarrowed`, for the rows this dialog holds. */
function addressesScope(rows: ScopedThesisRow[], ids: string[], scopeId: string): boolean {
  const selected = new Set(ids);
  return rows.some((row) => selected.has(row.id) && scopeOf(row).includes(scopeId));
}

/**
 * The per-scope measurement the feasibility contract speaks — global and each
 * task the rows address, measured against the tick. Mirrors
 * `StrategyFormDialog`'s `displayedInsightScopes`: the two id namespaces are
 * disjoint (ruling W13-R22), so a scope is starved on its own theses only.
 */
export function insightScopes(
  rows: ScopedThesisRow[],
  selectedIds: string[],
): FeasibilityInsightScope[] {
  if (rows.length === 0) return [];
  const checked = new Set(selectedIds);
  const measure = (scoped: ScopedThesisRow[]) => ({
    availableCount: scoped.length,
    selectedCount: scoped.filter((row) => checked.has(row.id)).length,
  });
  const scopes: FeasibilityInsightScope[] = [];
  const globalRows = rows.filter((row) => row.scope === 'global');
  if (globalRows.length > 0) scopes.push({ scope: 'global', ...measure(globalRows) });

  const names = taskNamesOf(rows);
  const seen = new Set<string>();
  for (const row of rows) {
    for (const taskId of row.taskIds) {
      if (seen.has(taskId)) continue;
      seen.add(taskId);
      const taskRows = rows.filter((candidate) => candidate.taskIds.includes(taskId));
      scopes.push({
        scope: 'task',
        taskId,
        taskName: names[taskId] ?? taskId,
        ...measure(taskRows),
      });
    }
  }
  return scopes;
}

/** One scope the tick left with nothing, and the sentence that says so. */
export interface StarvedInsightScope {
  /** `global` or the task id — what makes the row addressable (AC-W13-04). */
  scopeId: string;
  /** The feasibility contract's own wording, identical to the server's 400. */
  message: string;
  /** «Restore the strategy's theses of "Lose weight"» — the contained fix (MF-2). */
  fixLabel: string;
}

export interface StarvedScopeInput {
  rows: ScopedThesisRow[];
  /** What is ticked now. */
  selectedIds: string[];
  /** What the UNTOUCHED dialog holds — the state the operator started from. */
  preloadedIds: string[];
  /**
   * The ONE scope this block's run resolves to.
   * `BlockScreenGenerationContextResolver` ≈478 picks
   * `perTask[block.taskId]` when the block carries a task id and `global`
   * otherwise — no other scope reaches this block's prompt, so no other scope
   * can block this run (MF-2: the refusal must agree with the grounding panel's
   * single route). `undefined` = the block has no task id ⇒ the global scope.
   */
  blockTaskId?: string;
}

/**
 * Ruling HB5-R2 (fix round 2) — the refusal fires ONLY on a scope that
 *   (1) this block's run actually resolves to,
 *   (2) the preloaded selection addressed, and
 *   (3) the operator's edit left with nothing.
 *
 * (2) is what keeps the UNTOUCHED dialog enabled: a frozen narrowing of ten
 * GLOBAL theses addresses the global scope only, so the strategy's own task
 * scopes are unaddressed, keep their projection, and are not the operator's to
 * starve (MF-1 — the old guard refused the server's own resolution).
 */
export function starvedInsightScopes(input: StarvedScopeInput): StarvedInsightScope[] {
  const { rows, selectedIds, preloadedIds, blockTaskId } = input;
  if (rows.length === 0) return [];
  const scopeId = blockTaskId ?? GLOBAL_INSIGHT_SCOPE;
  const scope = insightScopes(rows, selectedIds)
    .find((candidate) => (candidate.taskId ?? GLOBAL_INSIGHT_SCOPE) === scopeId);
  // A scope with no rows at all is the analysis' business, never the tick's.
  if (!scope || scope.availableCount === 0) return [];
  if (scope.selectedCount > 0) return [];
  if (!addressesScope(rows, preloadedIds, scopeId)) return [];
  return [{
    scopeId,
    message: scopeWithoutThesesMessage(scope),
    // MF-2: the way out RESTORES what the strategy froze — it never inflates the
    // run by selecting every thesis of the analysis.
    fixLabel: scope.taskName
      ? `Restore the strategy's theses of "${scope.taskName}"`
      : 'Restore the strategy\'s theses',
  }];
}

/**
 * The refusal's one click: put ONE scope back to its PRELOADED ticks — the
 * strategy's own narrowing for that scope, never «select all» (MF-2). Every
 * other scope keeps whatever the operator chose.
 */
export function restoreScopeTheses(
  rows: ScopedThesisRow[],
  selectedIds: string[],
  preloadedIds: string[],
  scopeId: string,
): string[] {
  const preloaded = new Set(preloadedIds);
  const restored = rows
    .filter((row) => scopeOf(row).includes(scopeId) && preloaded.has(row.id))
    .map((row) => row.id);
  const next = new Set([...selectedIds, ...restored]);
  return rows.filter((row) => next.has(row.id)).map((row) => row.id);
}
